import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, AlertCircle, RefreshCw, Loader2, WifiOff } from "lucide-react";

/**
 * ULTRA-RESILIENT Speech-to-Text Hook - NEVER ABORTS
 */
export function useSpeechToText(options = {}) {
  const {
    language = 'en-US',
    continuous = true,
    interimResults = true,
    onTranscript = null,
    onError = null,
    maxSilenceMs = 30000,
    autoRestart = true,
    neverAbort = true,
    silentErrors = false,
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState("unknown");
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState("good");
  
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const restartAttempts = useRef(0);
  const isActiveRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const healthCheckIntervalRef = useRef(null);
  const lastSuccessfulRef = useRef(null);
  
  const maxRetries = neverAbort ? Infinity : 50;

  // Check if browser supports speech recognition
  const isSupported = typeof window !== 'undefined' && 
    !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const resetSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    if (maxSilenceMs > 0 && isActiveRef.current) {
      silenceTimerRef.current = setTimeout(() => {
        if (isActiveRef.current && recognitionRef.current) {
          try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
        }
      }, maxSilenceMs);
    }
  }, [clearSilenceTimer, maxSilenceMs]);

  const createRecognition = useCallback(() => {
    if (!isSupported) return null;
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.lang = language;
    recognition.maxAlternatives = 1;
    
    return recognition;
  }, [isSupported, continuous, interimResults, language]);

  const setupRecognitionHandlers = useCallback((recognition) => {
    if (!recognition) return;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      setIsReconnecting(false);
      setConnectionQuality("good");
      restartAttempts.current = 0;
      lastSuccessfulRef.current = Date.now();
      resetSilenceTimer();
    };

    recognition.onresult = (event) => {
      resetSilenceTimer();
      lastSuccessfulRef.current = Date.now();
      setConnectionQuality("good");
      
      let finalText = '';
      let interimText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;
        
        if (result.isFinal) {
          finalText += text + ' ';
        } else {
          interimText += text;
        }
      }

      if (finalText) {
        setTranscript(prev => {
          const newTranscript = prev + finalText;
          if (onTranscript) onTranscript(finalText.trim(), newTranscript);
          return newTranscript;
        });
        setInterimTranscript("");
      } else {
        setInterimTranscript(interimText);
      }
    };

    recognition.onerror = (event) => {
      clearSilenceTimer();
      
      let shouldRestart = (autoRestart || neverAbort) && isActiveRef.current;
      let errorMessage = null;

      switch (event.error) {
        case 'not-allowed':
        case 'permission-denied':
          errorMessage = "Microphone access denied.";
          setPermissionStatus("denied");
          shouldRestart = false;
          break;
        case 'no-speech':
          shouldRestart = isActiveRef.current;
          break;
        case 'audio-capture':
        case 'network':
        case 'service-not-allowed':
          setConnectionQuality("reconnecting");
          shouldRestart = isActiveRef.current;
          break;
        case 'aborted':
          shouldRestart = isActiveRef.current && neverAbort;
          break;
        default:
          setConnectionQuality("reconnecting");
          shouldRestart = isActiveRef.current;
      }

      if (errorMessage && !silentErrors) {
        setError(errorMessage);
        if (onError) onError(errorMessage, event.error);
      }

      if (shouldRestart && restartAttempts.current < maxRetries) {
        restartAttempts.current += 1;
        setIsReconnecting(true);
        
        const delay = Math.min(300 * Math.pow(1.5, Math.min(restartAttempts.current - 1, 10)), 10000);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isActiveRef.current && recognitionRef.current) {
            try { recognitionRef.current.start(); } catch (e) { /* ignore */ }
          }
        }, delay);
      } else if (!shouldRestart) {
        setIsListening(false);
        isActiveRef.current = false;
      }
    };

    recognition.onend = () => {
      clearSilenceTimer();
      setInterimTranscript("");
      
      if (isActiveRef.current && (autoRestart || neverAbort)) {
        setIsReconnecting(true);
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isActiveRef.current && recognitionRef.current) {
            try {
              recognitionRef.current.start();
              setIsReconnecting(false);
            } catch (e) {
              // Will retry via health check
              setIsListening(false);
            }
          } else {
            setIsListening(false);
          }
        }, 100);
      } else {
        setIsListening(false);
      }
    };
  }, [autoRestart, neverAbort, silentErrors, onTranscript, onError, clearSilenceTimer, resetSilenceTimer, maxRetries]);

  // Initialize on mount
  useEffect(() => {
    if (!isSupported) return;

    // Check permissions
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'microphone' })
        .then((result) => {
          setPermissionStatus(result.state);
          result.onchange = () => setPermissionStatus(result.state);
        })
        .catch(() => setPermissionStatus("unknown"));
    }

    // Create recognition
    const recognition = createRecognition();
    if (recognition) {
      setupRecognitionHandlers(recognition);
      recognitionRef.current = recognition;
    }

    // Health check
    healthCheckIntervalRef.current = setInterval(() => {
      if (isActiveRef.current && neverAbort && lastSuccessfulRef.current) {
        const timeSince = Date.now() - lastSuccessfulRef.current;
        if (timeSince > 60000) {
          // Force restart
          try { recognitionRef.current?.abort(); } catch (e) { /* ignore */ }
          const newRecognition = createRecognition();
          if (newRecognition) {
            setupRecognitionHandlers(newRecognition);
            recognitionRef.current = newRecognition;
            try { recognitionRef.current.start(); } catch (e) { /* ignore */ }
          }
          lastSuccessfulRef.current = Date.now();
        }
      }
    }, 5000);

    return () => {
      clearSilenceTimer();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (healthCheckIntervalRef.current) clearInterval(healthCheckIntervalRef.current);
      isActiveRef.current = false;
      try { recognitionRef.current?.abort(); } catch (e) { /* ignore */ }
    };
  }, [isSupported, neverAbort, createRecognition, setupRecognitionHandlers, clearSilenceTimer]);

  const startListening = useCallback(() => {
    if (!isSupported) return;
    
    isActiveRef.current = true;
    
    if (!recognitionRef.current) {
      const recognition = createRecognition();
      if (recognition) {
        setupRecognitionHandlers(recognition);
        recognitionRef.current = recognition;
      }
    }
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
      } catch (e) {
        if (!e.message?.includes('already started')) {
          const recognition = createRecognition();
          if (recognition) {
            setupRecognitionHandlers(recognition);
            recognitionRef.current = recognition;
            try { recognitionRef.current.start(); } catch (e2) { /* ignore */ }
          }
        }
      }
    }
  }, [isSupported, createRecognition, setupRecognitionHandlers]);

  const stopListening = useCallback(() => {
    isActiveRef.current = false;
    clearSilenceTimer();
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    try { recognitionRef.current?.stop(); } catch (e) { /* ignore */ }
    setIsListening(false);
    setIsReconnecting(false);
  }, [clearSilenceTimer]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  const clearTranscript = useCallback(() => {
    setTranscript("");
    setInterimTranscript("");
  }, []);

  const appendTranscript = useCallback((text) => {
    setTranscript(prev => prev + text);
  }, []);

  return {
    isListening,
    transcript,
    interimTranscript,
    isSupported,
    error,
    permissionStatus,
    isReconnecting,
    connectionQuality,
    startListening,
    stopListening,
    toggleListening,
    clearTranscript,
    appendTranscript
  };
}

// Voice Input Button Component
export function VoiceInputIndicator({ 
  isListening, 
  isReconnecting, 
  connectionQuality = "good",
  onClick, 
  className = "" 
}) {
  return (
    <Button
      type="button"
      variant={isListening ? "default" : "outline"}
      size="icon"
      onClick={onClick}
      className={`transition-all ${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : ''} ${className}`}
      title={isListening ? "Stop voice input" : "Start voice input"}
    >
      {isReconnecting ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isListening ? (
        <div className="flex items-center gap-1">
          <Mic className="w-4 h-4" />
          {connectionQuality === "reconnecting" && <WifiOff className="w-3 h-3" />}
        </div>
      ) : (
        <MicOff className="w-4 h-4" />
      )}
    </Button>
  );
}

// Full Speech-to-Text Button with transcript display
export function SpeechToTextButton({
  onTranscript,
  buttonClassName = "",
  showTranscript = false,
  neverAbort = true
}) {
  const {
    isListening,
    transcript,
    interimTranscript,
    isSupported,
    error,
    isReconnecting,
    connectionQuality,
    toggleListening,
    clearTranscript
  } = useSpeechToText({ 
    onTranscript,
    neverAbort,
    silentErrors: true
  });

  if (!isSupported) {
    return (
      <Button variant="outline" size="icon" disabled title="Speech not supported">
        <AlertCircle className="w-4 h-4 text-yellow-500" />
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <VoiceInputIndicator
        isListening={isListening}
        isReconnecting={isReconnecting}
        connectionQuality={connectionQuality}
        onClick={toggleListening}
        className={buttonClassName}
      />
      
      {showTranscript && (isListening || transcript) && (
        <div className="flex-1 min-w-0">
          <div className="text-sm">
            <span className="text-foreground">{transcript}</span>
            <span className="text-muted-foreground italic">{interimTranscript}</span>
          </div>
          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
      )}
      
      {transcript && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={clearTranscript}
          className="h-8 w-8"
        >
          <RefreshCw className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}

/**
 * Whisper Fallback Hook - Uses OpenAI Whisper for browsers without Web Speech API
 * (e.g., Firefox)
 */
export function useWhisperFallback(options = {}) {
  const {
    onTranscript = null,
    onError = null,
    context = 'journal',
    language = 'en'
  } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [whisperAvailable, setWhisperAvailable] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Check Whisper availability on mount
  useEffect(() => {
    const checkWhisper = async () => {
      try {
        const response = await fetch(`${API}/api/voice/check-whisper`);
        if (response.ok) {
          const data = await response.json();
          setWhisperAvailable(data.available);
        }
      } catch (e) {
        setWhisperAvailable(false);
      }
    };
    checkWhisper();
  }, [API]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : 'audio/mp4';
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await processWithWhisper(audioBlob, mimeType.split('/')[1]);
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setError(null);
    } catch (err) {
      const errorMsg = "Microphone access denied. Please enable microphone permissions.";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    }
  }, [onError]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const processWithWhisper = async (audioBlob, format) => {
    setIsProcessing(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);
      const audioBase64 = await base64Promise;

      // Send to Whisper endpoint
      const response = await fetch(`${API}/api/voice/transcribe-base64`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          audio_base64: audioBase64,
          format: format,
          language: language,
          context: context
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Transcription failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.text && onTranscript) {
        onTranscript(data.text);
      }
    } catch (err) {
      const errorMsg = err.message || "Transcription failed";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else if (!isProcessing) {
      startRecording();
    }
  }, [isRecording, isProcessing, startRecording, stopRecording]);

  return {
    isRecording,
    isProcessing,
    isSupported: whisperAvailable,
    error,
    startRecording,
    stopRecording,
    toggleRecording
  };
}

/**
 * Smart Speech Hook - Uses Web Speech API when available, falls back to Whisper
 */
export function useSmartSpeech(options = {}) {
  const webSpeechSupported = typeof window !== 'undefined' && 
    !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  // Use Web Speech API if available
  const webSpeech = useSpeechToText({
    ...options,
    // Disable if not supported
    ...(webSpeechSupported ? {} : { onTranscript: null })
  });

  // Use Whisper fallback if Web Speech not available
  const whisperFallback = useWhisperFallback({
    onTranscript: options.onTranscript,
    onError: options.onError,
    context: options.context || 'general',
    language: options.language?.split('-')[0] || 'en'
  });

  if (webSpeechSupported) {
    return {
      ...webSpeech,
      usingFallback: false,
      fallbackAvailable: whisperFallback.isSupported
    };
  }

  // Return Whisper-based interface matching Web Speech API
  return {
    isListening: whisperFallback.isRecording,
    isRecording: whisperFallback.isRecording,
    isProcessing: whisperFallback.isProcessing,
    transcript: "",
    interimTranscript: whisperFallback.isProcessing ? "Processing..." : "",
    isSupported: whisperFallback.isSupported,
    error: whisperFallback.error,
    permissionStatus: "unknown",
    isReconnecting: false,
    connectionQuality: "good",
    startListening: whisperFallback.startRecording,
    stopListening: whisperFallback.stopRecording,
    toggleListening: whisperFallback.toggleRecording,
    clearTranscript: () => {},
    appendTranscript: () => {},
    usingFallback: true,
    fallbackAvailable: true
  };
}

export default useSpeechToText;
